'use client';

import { Badge } from '@ahafood/ui/components/badge';
import { Button } from '@ahafood/ui/components/button';
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from '@ahafood/ui/components/card';
import { useDebounceCallback } from '@ahafood/ui/hooks/use-debounced-callback';
import { formatCurrency } from '@ahm/common-helpers';
import { EditIcon, MinusIcon, PlusIcon, StickyNote } from 'lucide-react';
import React, { useCallback, useMemo, useState, useEffect } from 'react';
import { useStoreItemSheet } from '@/app/[locale]/(order)/components/store-item/store-item-sheet-context';
import type { CartStore } from '@/store/cart/cart-store';
import { useCartStore } from '@/store/cart/cart-store-provider';
import {
  type CartItem as CartItemType,
  cartItemToMenuItem,
} from '@/types/item';

interface CartItemProps {
  item: CartItemType;
  onClose?: () => void;
}

export function CartItem({ item, onClose }: CartItemProps) {
  // Memoize selectors to prevent infinite loops with useCartStore
  const cartSelectors = useMemo(
    () => ({
      updateItemInCart: (state: CartStore) => state.updateItemInCart,
      removeItemFromCart: (state: CartStore) => state.removeItemFromCart,
    }),
    []
  );

  const updateItemInCart = useCartStore(cartSelectors.updateItemInCart);
  const removeItemFromCart = useCartStore(cartSelectors.removeItemFromCart);

  const { openSheet } = useStoreItemSheet();

  // Memoize the cartItemId extraction to avoid recalculation
  const cartItemId = useMemo(
    () => (item as any).cartItemId || item._id.toString(),
    [item]
  );

  // Memoize the price calculations once
  const { totalItemFee } = useMemo(() => {
    const basePrice = item.price * item.quantity;
    const addonsPrice =
      item.customizations?.reduce((acc, customization) => {
        const optionPrice = customization.selectedOption?.price ?? 0;
        return acc + optionPrice * item.quantity;
      }, 0) ?? 0;

    return {
      itemPrice: basePrice,
      customizationsFee: addonsPrice,
      totalItemFee: basePrice + addonsPrice,
    };
  }, [item.price, item.quantity, item.customizations]);

  const handleEditClick = useCallback(() => {
    // Create a menuItem from the cart item
    const menuItem = cartItemToMenuItem(item);

    if (onClose) {
      document.documentElement.dataset.drawerToSheetTransition = 'true';

      onClose();

      setTimeout(() => {
        openSheet(menuItem);
        setTimeout(() => {
          delete document.documentElement.dataset.drawerToSheetTransition;
        }, 100);
      }, 300);
    } else {
      openSheet(menuItem);
    }
  }, [item, openSheet, onClose]);

  const [localQuantity, setLocalQuantity] = useState(item.quantity);

  useEffect(() => {
    setLocalQuantity(item.quantity);
  }, [item.quantity]);

  const updateCartQuantity = useCallback(
    (newQuantity: number) => {
      if (newQuantity <= 0) {
        removeItemFromCart(cartItemId);
        onClose?.();
        return;
      }

      updateItemInCart(cartItemId, { quantity: newQuantity });
    },
    [cartItemId, removeItemFromCart, updateItemInCart, onClose]
  );

  const debouncedUpdateCart = useDebounceCallback(() => {
    updateCartQuantity(localQuantity);
  }, 500);

  const debouncedDecrement = useCallback(() => {
    if (localQuantity <= 1) {
      setLocalQuantity(0);
      removeItemFromCart(cartItemId);
      onClose?.();
      return;
    }

    const newQuantity = localQuantity - 1;
    setLocalQuantity(newQuantity);
    debouncedUpdateCart();
  }, [
    localQuantity,
    debouncedUpdateCart,
    removeItemFromCart,
    cartItemId,
    onClose,
  ]);

  const debouncedIncrement = useCallback(() => {
    const newQuantity = localQuantity + 1;
    setLocalQuantity(newQuantity);
    debouncedUpdateCart();
  }, [localQuantity, debouncedUpdateCart]);

  return (
    <Card className="mb-px border-none rounded-none shadow-none">
      <CardHeader className="p-4 pb-2">
        <div className="flex items-start justify-between w-full">
          <div className="flex-1 pr-2">
            <CardTitle className="line-clamp-2 leading-6 text-sm font-bold">
              {item.name}
            </CardTitle>
          </div>
          <div className="flex-none text-right">
            <CardTitle className="text-sm font-bold">
              {formatCurrency(totalItemFee)}
            </CardTitle>
          </div>
        </div>

        {(item.customizations?.length ?? 0) > 0 && (
          <div className="mt-1.5 max-h-24 overflow-y-auto text-xs">
            {(item.customizations ?? []).map((customization) => (
              <div
                key={customization._id}
                className="flex justify-between text-neutral-70"
              >
                <span className="truncate max-w-[70%]">
                  {customization.name}: {customization.selectedOption?.name}
                </span>
                {/* {(customization.selectedOption?.price ?? 0) > 0 && (
                  <span className="pl-2">
                    {formatCurrency(customization.selectedOption!.price)}
                  </span>
                )} */}
              </div>
            ))}
          </div>
        )}
        {item.note && (
          <Badge className="mt-2 bg-primary-10 hover:bg-primary-20 text-neutral-90 flex items-center gap-2 rounded-md">
            <StickyNote className="size-4 text-primary-40" />
            <span className="text-xs font-medium text-neutral-50 py-1 line-clamp-2">
              {item.note}
            </span>
          </Badge>
        )}
      </CardHeader>
      <CardContent className="grid grid-cols-2 gap-4 px-4 pb-3">
        <div className="col-span-1">
          <Button
            size="sm"
            variant="ghost"
            onClick={handleEditClick}
            className="-ml-2"
          >
            <EditIcon className="size-3 mr-1" /> Chỉnh sửa
          </Button>
        </div>
        <div className="col-span-1 text-right flex flex-row flex-nowrap justify-end items-center gap-2">
          <Button
            size="icon"
            variant="secondary"
            className="size-6 rounded-full"
            onClick={debouncedDecrement}
            aria-label="Giảm số lượng"
            disabled={localQuantity <= 0}
          >
            <MinusIcon className="size-3.5" />
          </Button>
          <div className="min-w-8 px-0.5 text-center font-bold text-neutral-90">
            {localQuantity}
          </div>
          <Button
            size="icon"
            className="size-6 rounded-full"
            onClick={debouncedIncrement}
            aria-label="Tăng số lượng"
          >
            <PlusIcon className="size-3.5" />
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}
